import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Alert,
  Divider,
  InputAdornment,
  Chip,
  Autocomplete,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Receipt as ReceiptIcon,
  LocalShipping as LocalShippingIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supplierInvoicesApi, deliveriesApi, plantsApi } from '../../services/api';

const EditInvoiceModal = ({ 
  open, 
  onClose, 
  invoice,
  onSuccess
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({});
  const [deliveries, setDeliveries] = useState([]);
  const [editingDeliveryId, setEditingDeliveryId] = useState(null);
  const [deliveryFormData, setDeliveryFormData] = useState({});
  const [deletedDeliveryIds, setDeletedDeliveryIds] = useState([]);
  const [error, setError] = useState(null);

  // Inizializza dati quando si apre il modal
  useEffect(() => {
    if (invoice && open) {
      setFormData({
        invoice_number: invoice.invoice_number || '',
        invoice_date: invoice.invoice_date ? invoice.invoice_date.split('T')[0] : '',
        due_date: invoice.due_date ? invoice.due_date.split('T')[0] : '',
        taxable_amount: invoice.taxable_amount || 0,
        vat_rate: invoice.vat_rate || 22,
        vat_amount: invoice.vat_amount || 0,
        total_amount: invoice.total_amount || 0,
        notes: invoice.notes || ''
      });
      setDeliveries(invoice.deliveries ? [...invoice.deliveries] : []);
      setDeletedDeliveryIds([]);
      setEditingDeliveryId(null);
      setError(null);
    }
  }, [invoice, open]);

  // Mutation per aggiornare fattura
  const updateInvoiceMutation = useMutation({
    mutationFn: (data) => supplierInvoicesApi.update(invoice.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier-invoices'] });
    }
  });

  // Mutation per aggiornare DDT
  const updateDeliveryMutation = useMutation({
    mutationFn: ({ id, data }) => deliveriesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliveries'] });
    }
  });

  // Mutation per eliminare DDT
  const deleteDeliveryMutation = useMutation({
    mutationFn: (id) => deliveriesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliveries'] });
    }
  });

  // Query per lista impianti
  const plantsQuery = useQuery({
    queryKey: ['plants-list'],
    queryFn: () => plantsApi.getAll({ limit: 1000 }),
    staleTime: 60000,
    enabled: open
  });

  const plants = plantsQuery.data?.data?.data || [];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return dateStr.split('T')[0];
  };

  // Ricalcola totali fattura basandosi sui DDT
  const recalculateTotals = (deliveryList = deliveries) => {
    const activeDeliveries = deliveryList.filter(d => !deletedDeliveryIds.includes(d.id));
    const totalTaxable = activeDeliveries.reduce((sum, d) => sum + (parseFloat(d.taxable_amount) || 0), 0);
    const totalVat = activeDeliveries.reduce((sum, d) => sum + (parseFloat(d.vat_amount) || 0), 0);
    const totalAmount = activeDeliveries.reduce((sum, d) => sum + (parseFloat(d.total_amount) || 0), 0);
    const totalLiters = activeDeliveries.reduce((sum, d) => sum + (parseFloat(d.quantity_liters) || 0), 0);

    setFormData(prev => ({
      ...prev,
      taxable_amount: totalTaxable.toFixed(2),
      vat_amount: totalVat.toFixed(2),
      total_amount: totalAmount.toFixed(2),
      quantity_liters: totalLiters.toFixed(2)
    }));
  };

  // Gestione modifica fattura
  const handleInvoiceChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Inizio modifica DDT
  const handleEditDelivery = (delivery) => {
    setEditingDeliveryId(delivery.id);
    setDeliveryFormData({
      ddt_number: delivery.ddt_number || '',
      delivery_date: formatDate(delivery.delivery_date),
      quantity_liters: delivery.quantity_liters || 0,
      price_per_liter: delivery.price_per_liter || 0,
      taxable_amount: delivery.taxable_amount || 0,
      vat_rate: delivery.vat_rate || 22,
      vat_amount: delivery.vat_amount || 0,
      total_amount: delivery.total_amount || 0,
      plant_id: delivery.plant_id || null,
      plant_code: delivery.plant_code || '',
      plant_address: delivery.plant_address || ''
    });
  };

  // Gestione selezione impianto
  const handlePlantSelect = (plant) => {
    if (plant) {
      setDeliveryFormData(prev => ({
        ...prev,
        plant_id: plant.id,
        plant_code: plant.delivery_code || plant.plant_code || '',
        plant_address: `${plant.indirizzo || ''}, ${plant.citta || ''}`.trim().replace(/^,\s*|,\s*$/g, '')
      }));
    } else {
      setDeliveryFormData(prev => ({
        ...prev,
        plant_id: null,
        plant_code: '',
        plant_address: ''
      }));
    }
  };

  // Gestione modifica campo DDT
  const handleDeliveryChange = (field, value) => {
    const newData = { ...deliveryFormData, [field]: value };
    
    // Ricalcola importi quando cambiano quantità o prezzo
    if (field === 'quantity_liters' || field === 'price_per_liter' || field === 'vat_rate') {
      const qty = parseFloat(field === 'quantity_liters' ? value : newData.quantity_liters) || 0;
      const price = parseFloat(field === 'price_per_liter' ? value : newData.price_per_liter) || 0;
      const vatRate = parseFloat(field === 'vat_rate' ? value : newData.vat_rate) || 22;
      
      const taxable = qty * price;
      const vat = taxable * (vatRate / 100);
      const total = taxable + vat;
      
      newData.taxable_amount = taxable.toFixed(2);
      newData.vat_amount = vat.toFixed(2);
      newData.total_amount = total.toFixed(2);
    }
    
    setDeliveryFormData(newData);
  };

  // Salva modifica DDT
  const handleSaveDelivery = () => {
    setDeliveries(prev => 
      prev.map(d => d.id === editingDeliveryId 
        ? { ...d, ...deliveryFormData }
        : d
      )
    );
    setEditingDeliveryId(null);
    
    // Ricalcola totali fattura
    const updatedDeliveries = deliveries.map(d => 
      d.id === editingDeliveryId ? { ...d, ...deliveryFormData } : d
    );
    recalculateTotals(updatedDeliveries);
  };

  // Annulla modifica DDT
  const handleCancelEditDelivery = () => {
    setEditingDeliveryId(null);
    setDeliveryFormData({});
  };

  // Elimina DDT (marca per eliminazione)
  const handleDeleteDelivery = (deliveryId) => {
    setDeletedDeliveryIds(prev => [...prev, deliveryId]);
    
    // Ricalcola totali escludendo il DDT eliminato
    const updatedDeliveries = deliveries.filter(d => d.id !== deliveryId && !deletedDeliveryIds.includes(d.id));
    recalculateTotals([...updatedDeliveries.filter(d => d.id !== deliveryId)]);
  };

  // Ripristina DDT eliminato
  const handleRestoreDelivery = (deliveryId) => {
    setDeletedDeliveryIds(prev => prev.filter(id => id !== deliveryId));
    recalculateTotals();
  };

  // Salva tutto
  const handleSave = async () => {
    try {
      setError(null);

      // 1. Elimina i DDT marcati per eliminazione
      for (const deliveryId of deletedDeliveryIds) {
        await deleteDeliveryMutation.mutateAsync(deliveryId);
      }

      // 2. Aggiorna i DDT modificati
      for (const delivery of deliveries) {
        if (!deletedDeliveryIds.includes(delivery.id)) {
          // Trova il DDT originale per confronto
          const originalDelivery = invoice.deliveries?.find(d => d.id === delivery.id);
          if (originalDelivery && JSON.stringify(originalDelivery) !== JSON.stringify(delivery)) {
            await updateDeliveryMutation.mutateAsync({
              id: delivery.id,
              data: {
                ddt_number: delivery.ddt_number,
                delivery_date: delivery.delivery_date,
                quantity_liters: parseFloat(delivery.quantity_liters),
                price_per_liter: parseFloat(delivery.price_per_liter),
                taxable_amount: parseFloat(delivery.taxable_amount),
                vat_rate: parseFloat(delivery.vat_rate),
                vat_amount: parseFloat(delivery.vat_amount),
                total_amount: parseFloat(delivery.total_amount),
                plant_id: delivery.plant_id || null,
                plant_code: delivery.plant_code,
                plant_address: delivery.plant_address
              }
            });
          }
        }
      }

      // 3. Aggiorna la fattura
      await updateInvoiceMutation.mutateAsync({
        invoice_number: formData.invoice_number,
        invoice_date: formData.invoice_date,
        due_date: formData.due_date,
        taxable_amount: parseFloat(formData.taxable_amount),
        vat_amount: parseFloat(formData.vat_amount),
        total_amount: parseFloat(formData.total_amount),
        quantity_liters: parseFloat(formData.quantity_liters || 0),
        notes: formData.notes
      });

      // Invalida tutte le query correlate
      queryClient.invalidateQueries({ queryKey: ['supplier-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['deliveries'] });

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Errore salvataggio:', err);
      setError(err.response?.data?.message || 'Errore durante il salvataggio');
    }
  };

  if (!invoice) return null;

  const activeDeliveries = deliveries.filter(d => !deletedDeliveryIds.includes(d.id));
  const markedForDeletion = deliveries.filter(d => deletedDeliveryIds.includes(d.id));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon color="primary" />
          <span>Modifica Fattura {invoice.invoice_number}</span>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Dati Fattura */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ReceiptIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="primary">Dati Fattura</Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Numero Fattura"
                    value={formData.invoice_number || ''}
                    onChange={(e) => handleInvoiceChange('invoice_number', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Data Fattura"
                    type="date"
                    value={formData.invoice_date || ''}
                    onChange={(e) => handleInvoiceChange('invoice_date', e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Data Scadenza"
                    type="date"
                    value={formData.due_date || ''}
                    onChange={(e) => handleInvoiceChange('due_date', e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Note"
                    value={formData.notes || ''}
                    onChange={(e) => handleInvoiceChange('notes', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Totali ricalcolati */}
              <Grid container spacing={2}>
                <Grid item xs={6} sm={2}>
                  <TextField
                    label="Imponibile"
                    value={formData.taxable_amount || 0}
                    InputProps={{
                      readOnly: true,
                      startAdornment: <InputAdornment position="start">€</InputAdornment>
                    }}
                    fullWidth
                    size="small"
                    variant="filled"
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    label="IVA"
                    value={formData.vat_amount || 0}
                    InputProps={{
                      readOnly: true,
                      startAdornment: <InputAdornment position="start">€</InputAdornment>
                    }}
                    fullWidth
                    size="small"
                    variant="filled"
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    label="Totale"
                    value={formData.total_amount || 0}
                    InputProps={{
                      readOnly: true,
                      startAdornment: <InputAdornment position="start">€</InputAdornment>
                    }}
                    fullWidth
                    size="small"
                    variant="filled"
                    sx={{ '& .MuiFilledInput-root': { bgcolor: 'primary.50' } }}
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    label="Litri Totali"
                    value={formData.quantity_liters || 0}
                    InputProps={{
                      readOnly: true,
                      endAdornment: <InputAdornment position="end">L</InputAdornment>
                    }}
                    fullWidth
                    size="small"
                    variant="filled"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* DDT Associati */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalShippingIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" color="primary">
                    DDT Associati ({activeDeliveries.length})
                  </Typography>
                </Box>
                {deletedDeliveryIds.length > 0 && (
                  <Chip
                    label={`${deletedDeliveryIds.length} da eliminare`}
                    color="error"
                    size="small"
                    icon={<WarningIcon />}
                  />
                )}
              </Box>
              
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>DDT</TableCell>
                      <TableCell>Data</TableCell>
                      <TableCell>Cod. Impianto</TableCell>
                      <TableCell>Indirizzo</TableCell>
                      <TableCell align="right">Litri</TableCell>
                      <TableCell align="right">€/L</TableCell>
                      <TableCell align="right">IVA %</TableCell>
                      <TableCell align="right">Imponibile</TableCell>
                      <TableCell align="right">IVA</TableCell>
                      <TableCell align="right">Totale</TableCell>
                      <TableCell align="center">Azioni</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeDeliveries.map((delivery) => (
                      <TableRow 
                        key={delivery.id}
                        sx={{ 
                          bgcolor: editingDeliveryId === delivery.id ? 'action.selected' : 'inherit'
                        }}
                      >
                        {editingDeliveryId === delivery.id ? (
                          // Modalità modifica
                          <>
                            <TableCell>
                              <TextField
                                value={deliveryFormData.ddt_number || ''}
                                onChange={(e) => handleDeliveryChange('ddt_number', e.target.value)}
                                size="small"
                                sx={{ width: 100 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="date"
                                value={deliveryFormData.delivery_date || ''}
                                onChange={(e) => handleDeliveryChange('delivery_date', e.target.value)}
                                size="small"
                                sx={{ width: 130 }}
                              />
                            </TableCell>
                            <TableCell colSpan={2}>
                              <Autocomplete
                                options={plants}
                                value={plants.find(p => p.id === deliveryFormData.plant_id) || null}
                                onChange={(e, newValue) => handlePlantSelect(newValue)}
                                getOptionLabel={(option) => 
                                  `${option.delivery_code || option.plant_code || ''} - ${option.indirizzo || ''}, ${option.citta || ''}`
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Impianto"
                                    size="small"
                                    placeholder="Cerca impianto..."
                                  />
                                )}
                                renderOption={(props, option) => (
                                  <li {...props} key={option.id}>
                                    <Box>
                                      <Typography variant="body2" fontWeight="bold">
                                        {option.delivery_code || option.plant_code || 'N/D'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {option.indirizzo}, {option.citta}
                                      </Typography>
                                    </Box>
                                  </li>
                                )}
                                isOptionEqualToValue={(option, value) => option.id === value?.id}
                                sx={{ minWidth: 280 }}
                                size="small"
                                loading={plantsQuery.isLoading}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                value={deliveryFormData.quantity_liters || ''}
                                onChange={(e) => handleDeliveryChange('quantity_liters', e.target.value)}
                                size="small"
                                sx={{ width: 80 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                value={deliveryFormData.price_per_liter || ''}
                                onChange={(e) => handleDeliveryChange('price_per_liter', e.target.value)}
                                size="small"
                                inputProps={{ step: 0.0001 }}
                                sx={{ width: 80 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                value={deliveryFormData.vat_rate || ''}
                                onChange={(e) => handleDeliveryChange('vat_rate', e.target.value)}
                                size="small"
                                sx={{ width: 60 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(deliveryFormData.taxable_amount)}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(deliveryFormData.vat_amount)}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(deliveryFormData.total_amount)}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Salva">
                                <IconButton size="small" color="success" onClick={handleSaveDelivery}>
                                  <SaveIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Annulla">
                                <IconButton size="small" color="default" onClick={handleCancelEditDelivery}>
                                  <CancelIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </>
                        ) : (
                          // Modalità visualizzazione
                          <>
                            <TableCell>{delivery.ddt_number || 'N/D'}</TableCell>
                            <TableCell>
                              {delivery.delivery_date ? new Date(delivery.delivery_date).toLocaleDateString('it-IT') : 'N/D'}
                            </TableCell>
                            <TableCell>{delivery.plant_code || 'N/D'}</TableCell>
                            <TableCell>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                {delivery.plant_address || 'N/D'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {parseFloat(delivery.quantity_liters || 0).toLocaleString('it-IT')}
                            </TableCell>
                            <TableCell align="right">
                              {delivery.price_per_liter ? `€ ${parseFloat(delivery.price_per_liter).toFixed(4)}` : 'N/D'}
                            </TableCell>
                            <TableCell align="right">{delivery.vat_rate || 22}%</TableCell>
                            <TableCell align="right">{formatCurrency(delivery.taxable_amount)}</TableCell>
                            <TableCell align="right">{formatCurrency(delivery.vat_amount)}</TableCell>
                            <TableCell align="right">{formatCurrency(delivery.total_amount)}</TableCell>
                            <TableCell align="center">
                              <Tooltip title="Modifica">
                                <IconButton 
                                  size="small" 
                                  color="primary" 
                                  onClick={() => handleEditDelivery(delivery)}
                                  disabled={editingDeliveryId !== null}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Elimina">
                                <IconButton 
                                  size="small" 
                                  color="error" 
                                  onClick={() => handleDeleteDelivery(delivery.id)}
                                  disabled={editingDeliveryId !== null}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}

                    {activeDeliveries.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={11} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            Nessun DDT associato
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* DDT marcati per eliminazione */}
              {markedForDeletion.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="warning" icon={<WarningIcon />}>
                    <Typography variant="subtitle2" gutterBottom>
                      DDT che verranno eliminati al salvataggio:
                    </Typography>
                    {markedForDeletion.map(d => (
                      <Box 
                        key={d.id} 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1, 
                          mt: 0.5 
                        }}
                      >
                        <Typography variant="body2">
                          DDT {d.ddt_number} - {parseFloat(d.quantity_liters || 0).toLocaleString('it-IT')} L - {formatCurrency(d.total_amount)}
                        </Typography>
                        <Button 
                          size="small" 
                          onClick={() => handleRestoreDelivery(d.id)}
                        >
                          Ripristina
                        </Button>
                      </Box>
                    ))}
                  </Alert>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSave}
          disabled={updateInvoiceMutation.isPending || updateDeliveryMutation.isPending || deleteDeliveryMutation.isPending || editingDeliveryId !== null}
          startIcon={<SaveIcon />}
        >
          {(updateInvoiceMutation.isPending || deleteDeliveryMutation.isPending) ? 'Salvataggio...' : 'Salva Modifiche'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditInvoiceModal;
